import React, { useContext, useState } from "react";
import axios from "axios";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { GoAlertFill } from "react-icons/go";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import toast from "react-hot-toast";
import Spinner from "../Assets/auth_spinner.svg";
import "../styles/LoginPage.css";

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [showPassword, setShowPassword] = useState(false);

  useGSAP(() => {
    gsap.from(".floating-label", {
      duration: 0.8,
      y: 50,
      opacity: 0,
      stagger: 0.1,
      ease: "power2.out",
    });

    gsap.from("#submitBtn", {
      duration: 1,
      scale: 0.5,
      opacity: 0,
      delay: 0.3,
      ease: "power2.out",
    });
  }, []);

  const {
    setName,
    setUserId,
    setPrimaryEmail,
    setProfileUrl,
    setUserRole,
    setLastLogin,
    setIsEligible,
    setIsVerified,
    setVerificationEmail,
    setHasSalesPersonRole,
    setAssignedBoardIds,
  } = useContext(UserContext);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleChange = (field, value) => {
    if (field === "email") {
      setEmail(value);
    } else {
      setPassword(value);
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFocus = (field) => {
    // Clear error when field gets focus
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const login = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/signin`,
        { email, password }
      );

      if (response?.data?.success) {
        if (response.data.data.userRole === "Student" && response.data.data?.verified === false) {
          setIsVerified(false);
          setVerificationEmail(response.data.data.email);
          navigate("/verify-otp");
          return;
        }

        localStorage.setItem("token", response.data.data.token);
        const currentDate = new Date();
        const formattedDate = currentDate.toLocaleString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        });
        setLastLogin(formattedDate);
        setUserId(
          response.data.data._id ||
            response.data.data.id ||
            response.data.data.userId ||
            "",
        );
        setName(response.data.data.name || "Guest User");
        setPrimaryEmail(response.data.data.email);
        setProfileUrl(response.data.data.profileURL || null);
        setUserRole(response.data.data.userRole);
        setHasSalesPersonRole(response.data.data.hasSalesPersonRole || false);
        setAssignedBoardIds(response.data.data.assignedBoardIds || []);

        toast.success(response.data.message || "Login successful");

        if (response.data.data.userRole === "Student") {
          setIsEligible(response.data.data.isEligible);
          navigate("/dashboard");
        }
        if (response.data.data.userRole === "Consultant")
          navigate("/consultant/dashboard");
        if (response.data.data.userRole === "Admin")
          navigate("/admin/dashboard");
        if (response.data.data.userRole === "SalesPerson")
          navigate("/sales/crm");
      }
    } catch (error) {
      setLoginError(
        error?.response?.data?.message ||
          "Unexpected error occurred, please try again"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (event) => {
    event.preventDefault();
    setTouched({ email: true, password: true });

    let hasError = false;
    const newErrors = { email: "", password: "" };

    if (!email.trim()) {
      newErrors.email = "Required";
      hasError = true;
    } else if (!validateEmail(email)) {
      newErrors.email = "Invalid email format";
      hasError = true;
    }

    if (!password.trim()) {
      newErrors.password = "Required";
      hasError = true;
    }

    setErrors(newErrors);
    if (!hasError) {
      login();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="grid grid-cols-2 w-full h-screen">
        {/* Left Side */}
        <div
          className="h-screen relative bg-cover bg-center bg-no-repeat flex items-center justify-center"
          style={{ backgroundImage: "url('/Assets/11.jpg')" }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <h1 className="text-4xl font-bold text-white z-10">
            Welcome To SocraticAli Consultants
          </h1>
        </div>

        {/* Right Side - Form */}
        <div className="flex items-center justify-center h-screen pr-10">
          <div className="p-8 bg-white shadow-lg rounded-lg h-auto w-[60%] mx-auto">
            <h1 className="text-2xl font-bold text-gray-800 mb-2 text-start">
              Sign In
            </h1>
            <p className="text-sm text-gray-500 mb-6 text-start">
              SocraticAli Consultant Portal
            </p>

            <form onSubmit={handleLogin} className="space-y-4 w-full">
              {/* Email */}
              <div className="floating-label">
                <input
                  type="email"
                  id="email"
                  placeholder=" "
                  className={`focus:ring-1 focus:ring-[#457B9D] ${
                    touched.email && errors.email ? "input-error" : ""
                  }`}
                  value={email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  onFocus={() => handleFocus("email")}
                />
                <label htmlFor="email">Email</label>
                <p
                  className={`helper-text ${
                    touched.email && errors.email ? "hidden" : ""
                  }`}
                >
                  Please enter your email
                </p>
                {touched.email && errors.email && (
                  <p className="error-message visible">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div className="floating-label relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder=" "
                  className={`focus:ring-1 focus:ring-[#457B9D] pr-10 ${
                    touched.password && errors.password ? "input-error" : ""
                  }`}
                  value={password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  onFocus={() => handleFocus("password")}
                />
                <label htmlFor="password">Password</label>
                <button
                  type="button"
                  className="absolute right-3 top-6 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </button>
                <p
                  className={`helper-text ${
                    touched.password && errors.password ? "hidden" : ""
                  }`}
                >
                  Please enter your password
                </p>
                {touched.password && errors.password && (
                  <p className="error-message visible">{errors.password}</p>
                )}
              </div>

              {loginError && (
                <div className="flex items-center gap-x-2 bg-red-100 border border-red-700 text-red-700 text-sm font-semibold px-4 py-4 rounded-md mb-4">
                  <GoAlertFill className="size-5" />
                  <p>{loginError}.</p>
                </div>
              )}

              {/* Forgot Password */}
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-[#457B9D] hover:underline text-sm font-semibold"
                >
                  Forgot Password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                id="submitBtn"
                className="w-full bg-[#1E3557] text-white px-4 py-2 rounded-md font-semibold relative"
              >
                Sign In
                {loading && (
                  <img
                    src={Spinner}
                    alt=""
                    className="size-6 absolute top-2 right-5"
                  />
                )}
              </button>
            </form>

            {/* Links */}
            <div className="flex justify-center text-sm text-gray-500 mt-4">
              <span>
                Don't Have An Account Yet?{" "}
                <button
                  className="text-[#457B9D] font-semibold"
                  onClick={() => navigate("/signup")}
                >
                  Sign Up
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
